/**
 * Plan Definitions & Limit Checking Utility
 * Dynamic Pricing Engine — plans come from API/sync, these are fallback helpers.
 */

/** Fallback plan definitions (used before API data loads) */
export const PLANS = {
  free: { maxProperties: 2, label: "Free" },
  starter: { maxProperties: 10, label: "Starter" },
  growth: { maxProperties: 999999, label: "Growth" },
  pro: { maxProperties: 999999, label: "Pro" }
};

/**
 * Check if user can add a new property.
 * @param {string} planType - plan id
 * @param {number} currentCount - Current property count
 * @param {number} [serverLimit] - Server-provided limit (overrides default)
 * @returns {boolean}
 */
export function canAddProperty(planType, currentCount, serverLimit) {
  const limit = serverLimit ?? PLANS[planType]?.maxProperties ?? 2;
  return currentCount < limit;
}

/**
 * Check if a feature is available on the current plan.
 * Uses the features array from sync data if available.
 * @param {string} featureKey - e.g. "leases", "whatsapp", "analytics"
 * @param {string[]} [planFeatures] - features array from plan data
 * @returns {boolean}
 */
export function hasFeature(featureKey, planFeatures) {
  if (!planFeatures || !Array.isArray(planFeatures)) return false;
  return planFeatures.includes(featureKey);
}

/**
 * Check if a feature is pro-only (for badge display).
 * Uses features array — if the feature isn't in the current plan's list, it's "locked".
 * @param {string} featureKey
 * @param {string[]} [planFeatures] - current plan's features
 * @returns {boolean}
 */
export function isProFeature(featureKey, planFeatures) {
  if (!planFeatures || !Array.isArray(planFeatures)) {
    // Fallback: these features are typically not in free plan
    return ["leases", "mapView", "fitInsights", "whatsapp", "csvExport", "analytics", "automation", "service_persons"].includes(featureKey);
  }
  return !planFeatures.includes(featureKey);
}

/**
 * Get plan usage info for display.
 * @param {string} planType - plan id
 * @param {number} currentCount - Current property count
 * @param {number} [serverLimit] - Server-provided limit
 * @returns {{ used: number, limit: number, pct: number, atLimit: boolean, label: string }}
 */
export function getPlanUsage(planType, currentCount, serverLimit) {
  const plan = PLANS[planType] || PLANS.free;
  const limit = serverLimit ?? plan.maxProperties;
  const isUnlimited = limit >= 999999;
  return {
    used: currentCount,
    limit: isUnlimited ? Infinity : limit,
    pct: isUnlimited ? 0 : (limit > 0 ? Math.min(100, Math.round((currentCount / limit) * 100)) : 100),
    atLimit: !isUnlimited && currentCount >= limit,
    label: plan.label
  };
}

/**
 * Format price from paise to display string.
 * @param {number} paise
 * @returns {string}
 */
export function formatPrice(paise) {
  if (!paise || paise <= 0) return "\u20B90";
  return `\u20B9${Math.round(paise / 100)}`;
}

/**
 * Friendly plan label for badges and tables.
 *
 * Resolves the display name with this precedence:
 *   1. Server-provided `planName` (best \u2014 comes from the JOIN that
 *      pulls .name from `plans` or `per_property_plans`).
 *   2. Hand-picked label for known fixed-tier ids (covers the case
 *      where the server didn't enrich, e.g. on legacy responses).
 *   3. Title-cased plan id with underscores stripped \u2014 so
 *      "per_property_v1" reads as "Per Property" rather than the
 *      raw "PER_PROPERTY_V1" that .toUpperCase() produces.
 *
 * @param {string} planId - the org's plan id (e.g. "free", "per_property_v1")
 * @param {string} [planName] - server-provided friendly name (optional)
 * @returns {string}
 */
export function formatPlanLabel(planId, planName) {
  if (planName && String(planName).trim()) return String(planName).trim();
  if (!planId) return "Free";
  const id = String(planId).toLowerCase();
  // Known fixed-tier shortcuts.
  if (id === "free") return "Free";
  if (id === "starter") return "Starter";
  if (id === "growth") return "Growth";
  if (id === "pro") return "Pro";
  if (id === "business") return "Business";
  if (id === "unlimited") return "Full Access";
  // Per-property plans usually have versioned ids
  // (per_property_v1, per_property_v2, ...). Collapse the family
  // to a single readable label.
  if (id.startsWith("per_property")) return "Per Property";
  // Generic fallback \u2014 title-case, underscores \u2192 spaces.
  return id
    .split("_")
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}
